export type TrackingConsent = 'accepted' | 'rejected' | null;

export type InternalTrackingTest = {
  enabled: boolean;
  hostname: string;
  allowedHostname: string;
  funnelCode: string;
};

export function isAnalyticsTestActive(test?: Pick<InternalTrackingTest, 'funnelCode'>): boolean {
  return test?.funnelCode === 'tst1';
}

export function isInternalTrackingTestActive(test?: InternalTrackingTest): boolean {
  if (!test?.enabled || test.funnelCode !== 'fc1') return false;
  const hostname = test.hostname.toLowerCase();
  return Boolean(test.allowedHostname) &&
    hostname === test.allowedHostname.toLowerCase();
}

export type MetaBrowserEventName =
  | 'Lead'
  | 'CompleteRegistration'
  | 'InitiateCheckout'
  | 'PageView'
  | 'ViewContent';

export type MetaQuizEventName =
  | 'AppStoreClicked'
  | 'GuestStepViewed'
  | 'QuizCompleted'
  | 'QuizProgress'
  | 'QuizStarted'
  | 'QuizStepCompleted'
  | 'QuizStepViewed';

export type MetaTrackingContext = {
  consent: Exclude<TrackingConsent, null>;
  eventId: string;
  eventSourceUrl?: string;
  fbc?: string;
  fbp?: string;
};

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

type PixelScript = {
  async: boolean;
  src: string;
};

type PixelDocument = {
  createElement: (tagName: 'script') => PixelScript;
  head: {
    appendChild: (script: PixelScript) => unknown;
  };
};

type PixelFunction = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  loaded?: boolean;
  push?: PixelFunction;
  queue?: unknown[][];
  version?: string;
};

type PixelTarget = Record<string, unknown> & {
  __focuscorpMetaPixelId?: string;
  _fbq?: PixelFunction;
  fbq?: PixelFunction;
};

type MetaPixelInitializationOptions = {
  consent?: TrackingConsent;
  documentRef?: PixelDocument;
  target?: PixelTarget;
};

type BuildMetaTrackingContextInput = {
  consent: TrackingConsent;
  cookieHeader?: string;
  eventId: string;
  eventSourceUrl?: string;
  fbclid?: string | null;
  nowMs?: number;
};

export const TRACKING_CONSENT_STORAGE_KEY = 'focuscorp_tracking_consent_v1';

const META_PIXEL_SOURCE = 'https://connect.facebook.net/en_US/fbevents.js';
const META_BROWSER_EVENT_NAMES = new Set<MetaBrowserEventName>([
  'Lead',
  'CompleteRegistration',
  'InitiateCheckout',
  'PageView',
  'ViewContent',
]);

function getDefaultStorage(): StorageLike | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function getDefaultPixelTarget(): PixelTarget | null {
  return typeof window === 'undefined' ? null : window as unknown as PixelTarget;
}

function getDefaultPixelDocument(): PixelDocument | null {
  return typeof document === 'undefined' ? null : document as unknown as PixelDocument;
}

export function normalizeTrackingConsent(value: unknown): TrackingConsent {
  return value === 'accepted' || value === 'rejected' ? value : null;
}

export function getTrackingConsent(
  storage: StorageLike | null = getDefaultStorage(),
  internalTest?: InternalTrackingTest,
): TrackingConsent {
  // Testing the funnel must never send events into the production analytics
  // projects, including when this browser previously accepted tracking.
  if (isAnalyticsTestActive(internalTest)) return 'rejected';
  // Internal test builds use a temporary measurement override. Never persist
  // this as a visitor's saved consent choice.
  if (isInternalTrackingTestActive(internalTest)) return 'accepted';

  if (!storage) {
    return null;
  }

  try {
    return normalizeTrackingConsent(storage.getItem(TRACKING_CONSENT_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function setTrackingConsent(
  choice: Exclude<TrackingConsent, null>,
  storage: StorageLike | null = getDefaultStorage(),
) {
  if (!storage) {
    return;
  }

  try {
    storage.setItem(TRACKING_CONSENT_STORAGE_KEY, choice);
  } catch {
    // Private browsing and locked-down browsers can reject storage writes.
  }
}

export function buildFbc(fbclid: string, createdAtMs: number): string | null {
  const normalized = fbclid.trim();

  if (!normalized || !Number.isFinite(createdAtMs) || createdAtMs <= 0) {
    return null;
  }

  return `fb.1.${Math.floor(createdAtMs)}.${normalized.slice(0, 500)}`;
}

export function readCookieValue(cookieHeader: string, name: string): string | null {
  const targetName = name.trim();

  if (!targetName) {
    return null;
  }

  for (const part of cookieHeader.split(';')) {
    const [rawName, ...rawValueParts] = part.trim().split('=');
    if (rawName !== targetName || rawValueParts.length === 0) {
      continue;
    }

    const rawValue = rawValueParts.join('=').trim();
    if (!rawValue) {
      return null;
    }

    try {
      return decodeURIComponent(rawValue);
    } catch {
      return rawValue;
    }
  }

  return null;
}

function normalizeEventSourceUrl(value: string | undefined) {
  if (!value) {
    return null;
  }

  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') {
      return null;
    }
    return `${url.origin}${url.pathname}`;
  } catch {
    return null;
  }
}

export function buildMetaTrackingContext({
  consent,
  cookieHeader = '',
  eventId,
  eventSourceUrl,
  fbclid = null,
  nowMs = Date.now(),
}: BuildMetaTrackingContextInput): MetaTrackingContext {
  const normalizedConsent = normalizeTrackingConsent(consent) ?? 'rejected';
  const context: MetaTrackingContext = {
    consent: normalizedConsent,
    eventId: eventId.trim().slice(0, 200),
  };

  if (normalizedConsent !== 'accepted') {
    return context;
  }

  const fbc = readCookieValue(cookieHeader, '_fbc') ?? (fbclid ? buildFbc(fbclid, nowMs) : null);
  const fbp = readCookieValue(cookieHeader, '_fbp');
  const normalizedUrl = normalizeEventSourceUrl(eventSourceUrl);

  if (fbc) {
    context.fbc = fbc.slice(0, 500);
  }
  if (fbp) {
    context.fbp = fbp.slice(0, 500);
  }
  if (normalizedUrl) {
    context.eventSourceUrl = normalizedUrl.slice(0, 500);
  }

  return context;
}

export function initializeMetaPixel(
  pixelId: string,
  options: MetaPixelInitializationOptions = {},
) {
  const normalizedPixelId = pixelId.trim();
  const consent = options.consent ?? getTrackingConsent();
  const target = options.target ?? getDefaultPixelTarget();
  const documentRef = options.documentRef ?? getDefaultPixelDocument();

  if (
    consent !== 'accepted' ||
    !/^\d+$/u.test(normalizedPixelId) ||
    !target ||
    !documentRef
  ) {
    return false;
  }

  if (target.__focuscorpMetaPixelId === normalizedPixelId) {
    return true;
  }

  let pixel = target.fbq;

  if (!pixel) {
    pixel = ((...args: unknown[]) => {
      if (pixel?.callMethod) {
        pixel.callMethod(...args);
      } else {
        pixel?.queue?.push(args);
      }
    }) as PixelFunction;
    pixel.push = pixel;
    pixel.queue = [];
    pixel.loaded = true;
    pixel.version = '2.0';
    target.fbq = pixel;
    target._fbq = pixel;

    const script = documentRef.createElement('script');
    script.async = true;
    script.src = META_PIXEL_SOURCE;
    documentRef.head.appendChild(script);
  }

  target.__focuscorpMetaPixelId = normalizedPixelId;
  pixel('init', normalizedPixelId);
  trackMetaPixelEvent('PageView', {}, undefined, target);
  return true;
}

export function trackMetaPixelEvent(
  name: MetaBrowserEventName,
  data: Record<string, unknown> = {},
  eventId?: string,
  target: PixelTarget | null = getDefaultPixelTarget(),
) {
  if (!target?.fbq || !META_BROWSER_EVENT_NAMES.has(name)) {
    return false;
  }

  // OpenBridge can forward browser-only events through CAPI too. Give every
  // occurrence an ID while retaining the server receipt for paired events.
  const id = eventId?.trim().slice(0, 200) || `browser:${crypto.randomUUID()}`;
  target.fbq('track', name, data, { eventID: id });

  return true;
}

export function trackMetaPixelCustomEvent(
  name: MetaQuizEventName,
  data: Record<string, unknown> = {},
  target: PixelTarget | null = getDefaultPixelTarget(),
  consent: TrackingConsent = getTrackingConsent(),
  eventId?: string,
) {
  if (consent !== 'accepted' || !target?.fbq) {
    return false;
  }

  target.fbq('trackCustom', name, data, { eventID: eventId?.trim().slice(0, 200) || `browser:${crypto.randomUUID()}` });

  return true;
}

export function trackMetaQuizHalfway(
  progressPercent: number,
  hasTrackedHalfway: boolean,
  target: PixelTarget | null = getDefaultPixelTarget(),
  consent: TrackingConsent = getTrackingConsent(),
) {
  if (hasTrackedHalfway || progressPercent < 50) {
    return hasTrackedHalfway;
  }

  return trackMetaPixelCustomEvent(
    'QuizProgress',
    { progress_percent: 50 },
    target,
    consent,
  );
}
